import {html, LitElement, PropertyValues} from 'lit';
import {customElement, property, query} from 'lit/decorators.js';
import {defaultSettings, Settings} from './models/fishman-login.model';
import {a11Fetch, A11SiteDataModel, FetchProps} from './utilities';

@customElement('fishman-login')
export class FishmanLogin extends LitElement {

    @property({type: String})
    default_username_value = '';

    @property({type: String})
    encodedSettings = '';

    @property({type: Object})
    settings: Settings = {...defaultSettings};

    @property({type: Boolean})
    is_captcha_required = false;

    @property({type: Boolean})
    is_form_submitting = false;

    @property({type: Boolean})
    is_in_set_password = false;

    @property({type: Boolean})
    is_in_register = false;

    @property({type: Boolean})
    is_in_verify_email = false

    @property({type: Boolean})
    is_successful = false;

    @property({type: Boolean})
    username_is_validated = false;

    @property({type: Boolean})
    has_error = false;

    @query('#recaptcha-container') recaptchaContainer!: HTMLDivElement;
    error_message!: string;
    failed_login_message!: string | undefined;
    private recaptchaWidget: number | null = null;

    public maybeResetCaptcha() {
        if (this.recaptchaWidget !== null && window.grecaptcha) {
            window.grecaptcha.reset(this.recaptchaWidget);
        } else {
            this.renderRecaptcha();
        }
    }

    override updated(changedProperties: PropertyValues) {
        if (!changedProperties.has('encodedSettings')) {
            return;
        }

        let decodedSettings;

        try {
            const rawSettings = atob(this.encodedSettings); // decode base64 string
            decodedSettings = JSON.parse(rawSettings); // parse the JSON string into an object
            this.settings = decodedSettings;
        } catch (e) {
            console.error('Cannot parse settings:', e);
        }

        if (decodedSettings?.is_in_verify_email) {
            this.is_in_verify_email = true;
            this.default_username_value = decodedSettings?.default_username_value;
        }

        if (decodedSettings?.is_in_register) {
            this.is_in_register = true;
            this.default_username_value = decodedSettings?.default_username_value;
        }
    }

    override async firstUpdated() {
        await this._enqueueCaptchaScript();

        sessionStorage.clear();
    }

    override render() {
        // Destructure the settings object for easy access to the properties
        const {
            allow_forgot_password,
            username_field_label,
            password_field_label,
            instructions,
            is_captcha_required,
            section_title,
            is_in_update_password,
            failed_login_message
        } = this.settings;

        const continueButtonLabel = this.username_is_validated ? 'Sign In' : 'Continue';

        this.failed_login_message = failed_login_message;

        if (this.is_successful) {
            return html`
                <div class="alert alert-success my-5" role="alert" data-test="fishman-login-success">
                    <strong>Welcome!</strong> Redirecting now...
                </div>
            `;
        }

        if (this.is_in_register) {
            return html`
                <fishman-register
                        .encodedSettings=${this.encodedSettings}
                        .default_username_value=${this.default_username_value}
                        @fishman-login-return-to-login=${this._handleReturnFromRegister}
                ></fishman-register>
            `;
        }

        if (this.is_in_set_password) {
            return html`
                <fishman-forgot-password
                        .encodedSettings=${this.encodedSettings}
                        .default_username_value=${this.default_username_value}
                        @fishman-login-return-to-login=${this._handleReturnFromForgotPassword}
                ></fishman-forgot-password>
            `;
        }

        if (this.is_in_verify_email) {
            return html`
                <fishman-verify-email
                        .encodedSettings=${this.encodedSettings}
                        .default_email_value=${this.default_username_value}
                        @fishman-login-return-to-login=${this._handleReturnFromVerifyEmail}
                ></fishman-verify-email>
            `;
        }

        if (is_in_update_password) {
            return html`
                <fishman-update-password
                        .encodedSettings=${this.encodedSettings}
                        .default_username_value=${this.default_username_value}
                ></fishman-update-password>
            `;
        }

        return html`
            <form class="row py-3 g-3" @submit=${this._onSubmit}>
                <div class="col-12 d-flex flex-column gap-3">

                    ${section_title ? html`<h1 class="fishman-login__section-title text-center">
                        ${section_title}</h1>` : ''}

                    <div>
                        ${username_field_label ? html`<label for="username"
                                                             class="form-label">${username_field_label}</label>` : ''}
                        <input type="email" name="username" id="username" class="form-control username-field"
                               .value=${this.default_username_value} data-test="username" required>
                    </div>

                    ${!this.username_is_validated ? html`<p class="fishman-login__term-agree-disclaimer">By continuing,
                        I agree to Fishman&rsquo;s <a href="/privacy-policy/">Privacy
                            Policy</a> and <a href="/terms-of-use/">Terms of Use</a></p>` : ''}

                    ${this.username_is_validated ? html`
                        <div>
                            ${password_field_label ? html`<label for="password"
                                                                 class="form-label">${password_field_label}</label>` : ''}
                            <input type="password" name="password" autocomplete="off" id="password" class="form-control"
                                   data-test="password" autofocus required>
                        </div>` : ''}

                    ${is_captcha_required || this.is_captcha_required ? html`
                        <div id="recaptcha-container"></div>
                    ` : null}

                    ${this.is_form_submitting ? html`
                        <div class="alert alert-info mb-0" role="alert"
                             data-test="fishman-login-validating-credentials">
                            Please wait while we validate your credentials.
                        </div>
                    ` : ''}

                    ${this.has_error ? html`
                        <div class="alert alert-danger mb-0" role="alert" data-test="fishman-login-error-login-failed"
                             .innerHTML=${this.error_message}>
                        </div>
                    ` : ''}

                    ${instructions && allow_forgot_password && this.username_is_validated ? html`
                        <div class="pt-3" .innerHTML=${instructions}></div>
                    ` : ''}

                    <p>
                        <button type="submit" class="btn btn-primary align-self-center"
                                ?disabled=${this.is_form_submitting} data-test="submit">
                            ${continueButtonLabel}
                        </button>
                    </p>


                    ${this.username_is_validated ? html`
                        <p>
                            <button type="button" class="btn btn-primary-outline align-self-center"
                                    @click=${this._handleCancelClick}
                                    data-test="cancel">
                                Cancel
                            </button>
                        </p>
                    ` : ''}

                </div>
            </form>
        `;
    }

    override createRenderRoot() {
        return this;
    }

    /**
     * Handles the click event for the forgot password button and outputs the fishman-forgot-password web component. Also,
     * sets the value of whatever the user has entered into the 'username' field to a variable that is passed down into
     * fishman-forgot-password. This way, fishman-forgot-password can pre-fill in the username with what the user had just entered.
     *
     * @param {Event} e The click event object.
     * @return {void}
     */
    private _handleForgotPasswordClick(e: Event) {
        e.preventDefault();

        const {allow_forgot_password} = this.settings;

        if (!allow_forgot_password) {
            return;
        }

        const emailInput = this.renderRoot.querySelector('.username-field') as HTMLInputElement;
        this.default_username_value = emailInput?.value;
        this.is_in_set_password = true;
    }

    /**
     * When a user is in the 'Forgot Password' component and clicks the 'return to login' link, an event is emitted from
     * the fishman-forgot-password component. This function handles when that event is emitted and shows the fishman-login form.
     * Also, it fills in the username field with whatever was currently in the forgot password component's username
     * field.
     *
     * @param {CustomEvent} e Event emitted from fishman-forgot-password
     */
    private _handleReturnFromForgotPassword = async (e: CustomEvent) => {
        this.is_in_set_password = false;
        this.default_username_value = e.detail.current_user_name;
        await this._setupForgotPasswordButtonClickListener();
    }

    private _handleReturnFromRegister = async (e: CustomEvent) => {
        this.is_in_register = false;
        this.default_username_value = e.detail.current_user_name;
        await this._setupForgotPasswordButtonClickListener();
    }

    private _handleReturnFromVerifyEmail = async (e: CustomEvent) => {
        this.is_in_verify_email = false;
        this.default_username_value = e.detail.current_user_name;
        await this._setupForgotPasswordButtonClickListener();
    }

    // When username is validated, focus the password field after the DOM updates
    private async _focusPasswordField() {
        await this.updateComplete;
        // microtask to ensure the new input is in the DOM
        await new Promise((r) => setTimeout(r, 0));
        const pwd = this.renderRoot.querySelector('#password') as HTMLInputElement | null;
        if (pwd) {
            pwd.focus();
        }
    }

    // Handle clicking cancel: revert to username step and focus username input
    private _handleCancelClick = async () => {
        this.username_is_validated = false;
        await this.updateComplete;
        const user = this.renderRoot.querySelector('#username') as HTMLInputElement | null;
        user?.focus();
    }

    private _enqueueCaptchaScript = async () => {
        if (typeof window.grecaptcha === 'undefined') {
            const script = document.createElement('script');
            script.src = `https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit`;
            script.async = true;
            script.defer = true;

            window.onRecaptchaLoad = () => {
                if (!this.settings.is_captcha_required) {
                    return;
                }

                this.renderRecaptcha();
            };

            document.head.appendChild(script);
        } else {
            this.renderRecaptcha();
        }
    }

    private renderRecaptcha() {
        const {captcha_public_site_key} = this.settings;

        if (this.recaptchaWidget === null && captcha_public_site_key) {
            this.recaptchaWidget = window.grecaptcha.render(this.recaptchaContainer, {
                sitekey: captcha_public_site_key,
                callback: (response: string) => {
                    this.dispatchEvent(new CustomEvent('recaptcha-success', {detail: response}));
                },
                'expired-callback': () => {
                    this.dispatchEvent(new CustomEvent('recaptcha-expired'));
                },
                'error-callback': () => {
                    this.dispatchEvent(new CustomEvent('recaptcha-error'));
                }
            });
        }
    }

    /**
     * The forgot password buttons are passed through to fishman-login as html, so there is no ability to add something
     * directly to the buttons such as a @change property. Instead, a click event listener must be attached to the
     * buttons dynamically. If a user flips between the fishman-login component and the fishman-forgot-password component, then
     * back to fishman-login, the handlers will be lost unless they're added each time the component is loaded. This function
     * allows fishman-login to attach the click listeners the first time the component loads, but also when a user goes in
     * between fishman-login and fishman-forgot-password.
     */
    private _setupForgotPasswordButtonClickListener = async () => {
        await new Promise((r) => setTimeout(r, 0));

        const toggle_password_buttons = this.renderRoot.querySelectorAll('.toggle-forgot-password');

        if (toggle_password_buttons && toggle_password_buttons.length > 0) {
            toggle_password_buttons.forEach(button => {
                button.addEventListener('click', this._handleForgotPasswordClick.bind(this));
            });
        }
    }

    private async _maybeLoginOrRegister(formData: FormData) {

        const fetchProperties: FetchProps = {
            action: 'verify_username_for_login_or_register',
            method: 'POST',
            body: formData,
        };

        const {errorMessage, result, statusCode} = await a11Fetch(fetchProperties);

        if (statusCode === 200) {
            switch (result.data.result) {
                case 'login':
                    this.username_is_validated = true;
                    await this._setupForgotPasswordButtonClickListener();
                    await this._focusPasswordField();
                    break;

                case 'register':
                    this.default_username_value = formData.get("username") as string
                    this.is_in_register = true;
                    break;
            }

            this.is_form_submitting = false;
            return;
        }

        if (result?.is_captcha_required) {
            this.is_captcha_required = true;
            await new Promise((r) => setTimeout(r, 0));
            this.maybeResetCaptcha();
        }

        if (this.failed_login_message && this.failed_login_message.trim() !== '') {
            this.error_message = this.failed_login_message;
        } else {
            this.error_message = errorMessage;
        }

        this.has_error = true;
        this.is_form_submitting = false;

    }

    private async _onSubmit(e: Event) {
        e.preventDefault();
        this.is_form_submitting = true;
        this.has_error = false;

        const {block_id, redirect_url} = this.settings;

        const form = e.target as HTMLFormElement;
        const formData = new FormData(form);

        if (!this.username_is_validated) {
            await this._maybeLoginOrRegister(formData);
            return;
        }

        const fetchProperties: FetchProps = {
            action: 'login_user_via_ajax',
            block_id,
            method: 'POST',
            body: formData,
        };

        const {errorMessage, result, statusCode} = await a11Fetch(fetchProperties);

        if (statusCode === 200) {

            switch (result.data.result) {
                case 'success':
                    window.location.href = redirect_url;
                    break;

                case 'pending':
                    this.default_username_value = formData.get("username") as string
                    this.is_in_verify_email = true;
                    this.is_form_submitting = false;
                    break;
            }


            this.is_form_submitting = false;
            return;
        }

        if (result.is_captcha_required) {
            this.is_captcha_required = true;
            await new Promise((r) => setTimeout(r, 0));
            this.maybeResetCaptcha();
        }

        if (this.failed_login_message && this.failed_login_message.trim() !== '') {
            this.error_message = this.failed_login_message;
        } else {
            this.error_message = errorMessage;
        }

        this.has_error = true;
        this.is_form_submitting = false;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'fishman-login': FishmanLogin;
    }

    interface Window {
        ai_site_data: A11SiteDataModel,
        grecaptcha: any,
        onRecaptchaLoad: () => void,
    }
}
